import { useState, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import api from '../../../../lib/api';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { formatDateOnly, formatMoney } from '../../../../lib/helpers';
import { FileText, Calendar, AlertCircle, User, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import RatingDisplay from '../../../../components/common/RatingDisplay';
import RateUser from '../../../../components/common/RateUser';

export default function CurrentContracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    api.get('/contracts/current')
      .then(res => setContracts(res.data.data))
      .catch(() => alert('No active contracts'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  if (loading) return <LoadingSpinner />;

  if (!contracts.length) {
    return (
      <div className="text-center py-20">
        <FileText size={80} className="mx-auto text-gray-300 mb-6" />
        <h2 className="text-3xl font-bold">No Active Contracts</h2>
        <p className="text-gray-600 mt-4">Hire a nanny to get started!</p>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-center text-4xl sm:text-5xl font-bold mb-12">Active Contracts</h1>

        {/* Carousel Container */}
        <div className="relative">
          <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
            <div className="flex">
              {contracts.map((contract) => (
                <div key={contract.id} className="flex-none w-full min-w-0 px-4 sm:px-6">
                  <div className="space-y-8">
                    {/* Start Date Card */}
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl p-10 sm:p-12 text-center">
                      <Calendar size={60} className="mx-auto mb-6" />
                      <p className="text-2xl">Contract Started</p>
                      <p className="text-4xl sm:text-5xl font-bold mt-3">{formatDateOnly(contract.start_date)}</p>
                    </div>

                    {/* Nanny + Salary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 transform transition hover:scale-105">
                        <h3 className="text-2xl sm:text-3xl font-bold mb-8 flex items-center gap-4 justify-center sm:justify-start">
                          <User className="text-primary" size={36} /> Your Nanny
                        </h3>
                        <div className="flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                          <div className="w-28 h-28 sm:w-32 sm:h-32 bg-primary text-white text-5xl sm:text-6xl font-bold rounded-3xl flex items-center justify-center shadow-lg overflow-hidden flex-shrink-0">
                            { contract.nanny.profile_picture && !contract.nanny.profile_picture.includes('default-avatar') ? 
                              <img
                                src={process.env.REACT_APP_IMAGE_URL+contract.nanny?.profile_picture}
                                alt={`${contract.nanny.full_name}`}
                                className="w-full h-full object-cover rounded-3xl"
                              /> : 
                              contract.nanny?.full_name?.[0]?.toUpperCase() || 'U'
                            }
                          </div>
                          <div className="flex-1">
                            <p className="text-2xl sm:text-3xl font-bold">{contract.nanny.full_name}</p>
                            <div className="my-4"><RatingDisplay rating={contract?.nanny?.rating} /></div>                                               
                            <p className="text-lg sm:text-xl text-gray-600 flex items-center justify-center sm:justify-start gap-3 mt-4">
                              <Phone size={24} /> {contract.nanny.phone_number}
                            </p>
                            <div className="mt-6">
                              <RateUser userId={contract?.nanny?.id} />
                            </div> 
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 transform transition hover:scale-105">
                        <h3 className="text-2xl sm:text-3xl font-bold mb-8">Salary & Terms</h3>
                        <p className="text-4xl sm:text-5xl font-black text-primary">
                          {formatMoney(Number(contract.monthly_salary))}
                          <span className="text-xl sm:text-2xl text-gray-600 font-normal"> /month</span>
                        </p>
                        <p className="text-base sm:text-lg text-gray-700 mt-8 leading-relaxed whitespace-pre-line">
                          {contract.terms}
                        </p>
                      </div>
                    </div>

                    {/* Terminate Button */}
                    <div className="text-center pt-8">
                      <button className="px-12 sm:px-16 py-5 sm:py-6 bg-red-600 text-white text-xl sm:text-2xl font-bold rounded-3xl hover:bg-red-700 transition transform hover:scale-105 shadow-xl">
                        <AlertCircle className="inline mr-4" size={28} sm={32} />
                        Terminate Contract
                      </button>
                      <p className="text-gray-500 text-base sm:text-lg mt-6">Only use if absolutely necessary</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-8 sm:-translate-x-12 bg-white/90 hover:bg-white rounded-full p-3 sm:p-4 shadow-2xl transition disabled:opacity-30"
            onClick={scrollPrev}
            disabled={selectedIndex === 0}
          >
            <ChevronLeft size={36} sm={40} />
          </button>

          <button
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-8 sm:translate-x-12 bg-white/90 hover:bg-white rounded-full p-3 sm:p-4 shadow-2xl transition disabled:opacity-30"
            onClick={scrollNext}
            disabled={selectedIndex === contracts.length - 1}
          >
            <ChevronRight size={36} sm={40} />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-3 mt-12">
            {contracts.map((_, idx) => (
              <button
                key={idx}
                className={`w-4 h-4 rounded-full transition-all ${
                  idx === selectedIndex
                    ? 'bg-primary w-12'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                onClick={() => emblaApi && emblaApi.scrollTo(idx)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}